import React, { useEffect, useState } from "react";
import { Download, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const shortDate = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const BuyerLeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    setBusy(true); setError("");
    api.get("/admin/buyer-updates").then(({ data }) => setLeads(data)).catch((e) => setError(e.response?.data?.detail || "Could not load buyer sign-ups")).finally(() => setBusy(false));
  };
  useEffect(load, []);

  const del = async (l) => {
    if (!window.confirm(`Remove ${l.email} from the buyer updates list?`)) return;
    try { await api.delete(`/admin/buyer-updates/${l.id}`); toast.success(`Removed ${l.email}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/buyer-updates/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `buyer-updates-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const needle = q.trim().toLowerCase();
  const shown = needle ? leads.filter((l) => (l.email || "").toLowerCase().includes(needle) || (l.name || "").toLowerCase().includes(needle)) : leads;

  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="buyer-leads-manager">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-serif text-xl text-[#11211A]">Buyer updates sign-ups ({shown.length}{needle ? ` / ${leads.length}` : ""})</div>
          <p className="text-xs text-[#7D8C85] mt-1">Hotel buyers who clicked "Keep me updated" on /for-hotels. Confirmation email is sent via Resend on first sign-up.</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="px-3 py-1.5 text-xs border border-[#C4CFC9] bg-white min-w-[220px]" data-testid="buyer-leads-search" />
          <button type="button" onClick={load} className="btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="buyer-leads-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          <button type="button" onClick={exportCsv} disabled={leads.length === 0} className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="buyer-leads-export"><Download className="w-3.5 h-3.5" /> Export CSV</button>
        </div>
      </div>
      {error && <div className="text-sm text-[#B35C4A] mb-3" data-testid="buyer-leads-error">{error}</div>}
      {busy ? <div className="text-sm text-[#7D8C85]">Loading…</div> : shown.length === 0 ? (
        <div className="text-sm text-[#7D8C85] py-8 text-center" data-testid="buyer-leads-empty">{needle ? `No sign-ups match "${q}".` : "No buyer sign-ups yet."}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="buyer-leads-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-widest text-[#7D8C85] border-b border-[#E8ECE9]">
              <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Signed up</th><th className="py-2 pr-3">Confirmation</th><th className="py-2 pr-3">Submissions</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l.id} className="border-b border-[#F0EFEA]" data-testid={`buyer-lead-${l.id}`}>
                  <td className="py-2 pr-3 text-[#11211A]">{l.name}</td>
                  <td className="py-2 pr-3"><a href={`mailto:${l.email}`} className="text-[#264839] hover:underline">{l.email}</a></td>
                  <td className="py-2 pr-3 text-[#7D8C85] whitespace-nowrap">{shortDate(l.created_at)}</td>
                  <td className="py-2 pr-3"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${l.email_status === "sent" ? "text-[#264839] border-[#264839]/40" : "text-[#B35C4A] border-[#B35C4A]/40"}`}>{l.email_status || "—"}</span></td>
                  <td className="py-2 pr-3 text-[#7D8C85]">{l.submissions || 1}</td>
                  <td className="py-2 text-right"><button type="button" onClick={() => del(l)} className="text-[#B35C4A] hover:underline inline-flex items-center gap-1 text-xs" data-testid={`buyer-lead-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BuyerLeadsManager;
